<!-- The row under a ticket on Today: the two things to do with it next.

     Today is a betting board now, so a ticket's action is on the board rather
     than a screen away. A proposed ticket has TWO, and they are the two halves
     of one act: go and put it on, then say you did. "Open at Neds" copies the
     slip and opens the book; "Place bet" writes the record here. For a long
     time only the second existed, which made Legwork an app that asked you to
     record a bet it gave you no way to make.

     ONCE IT IS PLACED, ONLY "Place bet" GOES. There is no second record to
     write — but the book is still where the bet now lives, so the way to it
     stays, beside the legs to hand over and the way to the record just
     written. A punter checking whether a leg voided overnight should not have
     to find the app's own front door to get to Neds.

     THE HANDOFF HAPPENS IN THIS COMPONENT rather than in the page above it.
     It needs the ticket's legs and the profile's default book, and this row
     sits two components below either screen that could hand them down — so it
     reads both from the store, the way ProposedStub and PricesBar do. What it
     does NOT do is decide anything: which book, what the slip says and what the
     toast reads are all `lib/bookmakers.js` and `slipText`, so they can be
     pinned in the node suite.

     It lives outside <Ticket>, never inside. The ticket's root is a <button>
     when it is interactive, and a button inside a button is not a thing the
     browser will render.

     Ionic strips `aria-label` off an `ion-button` host, so every label here is
     visible text. That is the right answer anyway: "Place bet" said out loud
     and "Place bet" read on the paper are the same three words.

     On a break there is no action at all — see `disabled` below. Today never
     reaches that case (it prints one sheet where the whole feed was), so this
     is for the screens that keep a ticket on the paper while a break is on. -->
<template>
  <div v-if="disabled" class="ticket-actions ticket-actions--off" :class="rowClass">
    <p v-if="note" class="small-print off-note">{{ note }}</p>
  </div>

  <div v-else-if="status === 'proposed'" class="ticket-actions ticket-actions--proposed" :class="rowClass">
    <!-- The primary, because it is the one that actually gets money on: the
         slip goes to the clipboard and the book opens — at the page that quoted
         the leg where the feed gave us one, at its front door where it did not
         (`openTarget`, lib/bookmakers.js). The words change with it: "Add to
         Neds slip" only where the link is a betslip link. -->
    <IonButton
      v-if="book"
      class="action action--open"
      expand="block"
      :disabled="opening"
      @click="openBook"
    >{{ openLabel(book, target) }}</IonButton>
    <!-- Second, and outlined, because it does not place anything: it writes the
         record of a bet that is already on. -->
    <IonButton
      class="action action--place"
      expand="block"
      :fill="book ? 'outline' : 'solid'"
      @click="emit('place')"
    >Place bet</IonButton>
    <!-- THE THIRD THING TO DO WITH A TICKET: nothing, for good. It is `clear`
         and it does not stretch, because it is not a peer of the two beside it
         — those are the halves of placing a bet, this is the way to stop
         reading about one. On a ticket the punter built it says Delete, and it
         means it: see `hide-copy.js`. -->
    <IonButton
      v-if="canHide"
      class="action action--hide"
      fill="clear"
      @click="hide"
    >{{ hideLabel(multi) }}</IonButton>
  </div>

  <div v-else-if="status === 'placed'" class="ticket-actions ticket-actions--placed" :class="rowClass">
    <!-- THE WAY TO THE BOOKIE OUTLIVES THE BET. It used to go the moment a
         ticket was placed, on the reasoning that the handoff was finished —
         but the punter's reason to open Neds does not end when the money goes
         on. It starts there: is it actually on, what did it settle at, has a
         leg been voided. So the button stays, the slip still goes to the
         clipboard with it, and only "Place bet" leaves — there is no second
         record to write.
         Outline and small here, where the proposed row has it solid: on a
         placed ticket nothing on this row commits anything, and a solid
         control would be the app asking for a tap it does not need. -->
    <IonButton
      v-if="book"
      class="action action--open"
      fill="outline"
      size="small"
      :disabled="opening"
      @click="openBook"
    >{{ openLabel(book, target) }}</IonButton>
    <!-- Copy legs stays on the ticket screen, where the legs are the reason to
         be there. In the feed a placed ticket is a record among many, and the
         one thing to do with it is find it in History. -->
    <IonButton v-if="density !== 'feed'" class="action" fill="outline" size="small" @click="emit('copy')">Copy legs</IonButton>
    <IonButton class="action" fill="clear" size="small" @click="emit('history')">In History ›</IonButton>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { IonButton } from '@ionic/vue';
import { removeWithUndo, toast } from '../core/ui';
import { haptic } from '../core/util.js';
import { openExternal } from '../core/native.js';
import { copyText, COPY_FAILED } from '../lib/clipboard.js';
import { slipText } from '../lib/tickets.js';
import {
  handoffBook, openLabel, openTarget, rememberOpened, slipCopiedLine,
} from '../lib/bookmakers.js';
import { DELETED_LINE, HIDDEN_LINE, hideLabel } from '../lib/hide-copy.js';
import {
  deleteBuilt, findTicket, hideTicket, riskProfile, sampleMode, unhideTicket,
} from '../store.js';

const props = defineProps({
  /**
   * The multi this row belongs to, or null. A void ticket and a blank one both
   * render nothing: there is no bet to place and no record to visit, and a
   * disabled button would only be a question the pad has already answered.
   */
  multi: { type: Object, default: null },
  /**
   * Beside a `compact` ticket. The row's whole job is to sit under the ticket
   * it belongs to, so when the ticket's own paper shrinks the pull-up has to
   * shrink with it — a -24px reach into a 12px margin lands the button on top
   * of the last leg.
   */
  compact: { type: Boolean, default: false },
  /**
   * Today's feed. The row keeps every control it had — nothing a punter presses
   * is worth saving height on — but Place bet comes down to 44, and the pull-up
   * becomes a plain --sp-2 gap because there is no longer a --sp-4 margin above
   * it to reach into.
   */
  density: { type: String, default: 'default' },
  /**
   * Nothing is to be done with this ticket — a break is on (`lib/limits.js`).
   *
   * The actions are TAKEN AWAY, never greyed out: a disabled Place bet is a
   * button somebody taps twice to find out why, and the answer belongs in
   * words. `note` is those words, and where a screen has already said them
   * once — Today prints one sheet in place of the whole feed — it is left
   * blank and this row is simply the gap where the button was.
   */
  disabled: { type: Boolean, default: false },
  /** The line printed where the actions were. Optional; see `disabled`. */
  note: { type: String, default: '' },
  /**
   * Whether Hide is offered. The store's hide works on the DEALT pad; a ticket
   * from the ask screen (components/ask) hides itself from its own answer
   * instead, so its row turns this one off rather than offering a no-op.
   */
  hideable: { type: Boolean, default: true },
});

const emit = defineEmits(['place', 'copy', 'history']);

const status = computed(() => props.multi?.status ?? null);

const rowClass = computed(() => ({
  'ticket-actions--compact': props.compact && props.density !== 'feed',
  'ticket-actions--feed': props.density === 'feed',
}));

// --- the handoff --------------------------------------------------------------

/**
 * Which book this ticket goes to. A single names its own (`bookmakerKey`); a
 * multi has no such claim and goes to the profile's default. Null only where
 * the profile points at a book this app has no address for, and then the row is
 * just Place bet — a button with nowhere to go is worse than no button.
 */
const book = computed(() => handoffBook(props.multi, riskProfile.value?.bookmaker));

/** The legs, off the same timeline the ticket above this row was printed from. */
const legs = computed(() => findTicket(props.multi?.id)?.legs ?? []);

/**
 * WHERE THE TAP GOES, and therefore what the button says. The leg's own page at
 * the book on a ticket that names one, the front door otherwise; the label and
 * the toast both read it, so the words and the URL cannot disagree.
 */
const target = computed(() => openTarget(book.value, legs.value));

const opening = ref(false);

/**
 * Copy, say so, leave.
 *
 * In that order, and the copy is first because it has to start inside the tap:
 * the old-WebView fallback in `clipboard.js` is a `execCommand` on a textarea
 * and a browser will refuse it outside a user gesture.
 *
 * A failed copy does NOT stop the trip. Somebody who tapped "Open at Neds"
 * asked to go to Neds; the toast tells them the slip did not come with them,
 * which is a thing they can fix by hand once they are there.
 */
async function openBook() {
  const b = book.value;
  if (!b || opening.value) return;
  const to = target.value;
  opening.value = true;
  haptic('tick');
  try {
    await copyText(slipText(props.multi, legs.value));
    toast(slipCopiedLine(b, to));
  } catch (e) {
    toast(e?.message ? String(e.message) : COPY_FAILED);
  }
  // So the Place alert can say "Opened Neds · tap Place bet when it's on."
  // rather than its usual line — the two taps are minutes and one app apart.
  rememberOpened(props.multi?.id, b);
  // The leg's own page where there is one, the front door where there is not.
  // Still an https URL either way, so the OS still resolves it to the installed
  // app — see the top of lib/bookmakers.js.
  await openExternal(to.url);
  opening.value = false;
}

// --- hide, and delete ----------------------------------------------------------
// The act is the store's (`hideTicket` / `deleteBuilt`); the words are
// `lib/hide-copy.js`'s; what is left here is which of the two this ticket gets
// and how the way back is offered.

/**
 * A ticket the sample board printed is a fixture, and hiding one would write a
 * key for a pad nobody can bet on. The break's own guard is `disabled` above,
 * which takes the whole row away.
 */
const canHide = computed(() => props.hideable && !!props.multi?.id && !sampleMode.value);

/**
 * HIDE IS REVERSIBLE AND SAYS SO; DELETE IS NOT, AND IS DEFERRED.
 *
 * Hiding writes one key: the undo puts it back, and the foot of the feed
 * offers the same thing for as long as the day lasts. Deleting takes the
 * punter's own draft off the trail as well, which nothing can undo — so it
 * goes through `removeWithUndo`, which is exactly what that is for: the ticket
 * leaves the feed on the tap (the hide), and the irreversible half runs when
 * the window closes. A reload inside the window leaves the draft hidden and
 * its row on the trail, which is where a built ticket stood anyway.
 */
function hide() {
  const id = props.multi?.id;
  if (!id) return;
  haptic('pop');
  try {
    if (!hideTicket(id)) return;
  } catch (e) {
    toast(e?.message ? String(e.message) : 'Couldn’t hide that ticket.');
    return;
  }
  if (props.multi?.builtByHand === true) {
    removeWithUndo({
      text: DELETED_LINE,
      commit: () => deleteBuilt(id),
      revert: () => unhideTicket(id),
    });
    return;
  }
  removeWithUndo({ text: HIDDEN_LINE, revert: () => unhideTicket(id) });
}
</script>

<style scoped>
/* Pulled up under the ticket it belongs to. `Ticket` carries a full --sp-6 of
   margin so one ticket is clearly torn from the next, and these buttons are
   part of the ticket above them, not the start of the one below. */
.ticket-actions {
  margin: calc(-1 * var(--sp-5)) 0 var(--sp-6);
}

/* Today: the ticket above carries a --sp-4 margin rather than a --sp-6 one, so
   the reach up into it is a third of what it was and what follows sits closer
   behind. Same relationship, a smaller scale. */
.ticket-actions--compact {
  margin: calc(-1 * var(--sp-3)) 0 var(--sp-4);
}

/* Three controls at 320: "Open at Ladbrokes", "Copy legs" and "In History ›"
   do not fit on one line at the narrowest width the app is drawn at, and a row
   that does not fit is a row that scrolls the page sideways. So it WRAPS —
   each button keeps its own words whole and the row becomes two lines rather
   than one line with a control off the end of it. `min-width: 0` so a long
   book name shrinks its own button instead of pushing the row wider than the
   sheet. */
.ticket-actions--placed {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--sp-2);
}

.ticket-actions--placed .action {
  min-width: 0;
}

/* Open at Neds · Place bet, on ONE row at every width this app is drawn at.
   Stacked they would be two full-width commits under a ticket that is 244px
   tall, and the pair would read as the page's furniture rather than as the two
   halves of one act. `flex: 1 1 0` rather than `auto` so the longest book name
   in the table (Betfair Exchange) cannot push Place bet off the end: both
   halves take the same width whatever is written on them, and the text wraps
   inside its own button if it must. With no book to open, Place bet is the only
   child and takes the row on its own, exactly as it did before. */
.ticket-actions--proposed {
  display: flex;
  align-items: stretch;
  gap: var(--sp-2);
}

.ticket-actions--proposed {
  /* Three controls at 320 — "Open at Ladbrokes", "Place bet", "Hide" — will
     not always make one line, and a row that does not fit scrolls the page
     sideways. The pair wraps together before anything leaves the sheet. */
  flex-wrap: wrap;
}

.ticket-actions--proposed .action {
  flex: 1 1 0;
  min-width: 0;
}

/* NOT A PEER OF THE TWO BESIDE IT. Open and Place are the two halves of one
   act and share the row equally; Hide is the way to stop reading about the
   ticket at all, so it takes only the words it needs and no ink. Still 44px
   tall and still a real target — quiet is not the same as small. */
.ticket-actions--proposed .action--hide {
  flex: 0 0 auto;
  --color: var(--carbon-faint);
  --padding-start: var(--sp-2);
  --padding-end: var(--sp-2);
}

.ticket-actions--proposed .action--hide::part(native) {
  min-height: 44px;
}

/* The primary role floors at 52 and the secondary at 44, which side by side is
   a row with a step in it. `stretch` above equalises the HOSTS; this lets each
   native fill the host it is in, so the pair is flush at whichever of the two
   heights the density has settled on. */
.ticket-actions--proposed .action::part(native) {
  height: 100%;
}

/* A break, said once and quietly. No control, no colour and nothing to press —
   the row keeps its place in the rhythm so the tickets above and below it do not
   shuffle up when the break starts. */
.off-note {
  margin: 0;
}

/* Margin only. `.action--place` used to pin Place bet to `height: var(--tap)`
   — a hard 48px — which now CAPS the primary role's 52px rather than merely
   agreeing with it, and a capped primary is a primary drawn as a secondary.
   The role owns the height of every button in this row; this owns where the
   row sits. */
.action {
  margin: 0;
}

/* --- feed density ----------------------------------------------------------- */
/* No reach upward at all: the feed's ticket stops --sp-3 above this, and a
   negative margin there would land the button across the last leg. */
.ticket-actions--feed {
  /* --sp-2 down from the legs, --sp-3 before the next tear: the same --sp-3 the
     tear keeps on its other side, so a ticket ends the way the next begins. */
  margin: var(--sp-2) 0 var(--sp-3);
}

/* PLACE BET AT 44, NOT AT THE PRIMARY ROLE'S 52.
   The 52 is written for a screen with ONE commit on it — Settings, the ticket
   screen — where the tallest control is the point. A feed has one per ticket,
   three or four to a screen, and at 52 they stop reading as "the thing to do
   with this ticket" and start reading as the page's furniture. 44 is the floor
   WCAG 2.2 SC 2.5.8 sets and the height every other target on this screen
   already takes, so nothing is lost but the bulk.
   `!important` because the role's own floor is one: app.css raises
   `::part(native)` to 52 with the flag, and only the same weight at a higher
   specificity can lower it again. The ink, the radius, the type and the press
   are all still the primary role's. */
.ticket-actions--feed .action.button-solid::part(native) {
  min-height: 44px !important;
  --padding-top: var(--sp-2);
  --padding-bottom: var(--sp-2);
}
</style>
